import { FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Assessment,
  AssessmentsSortByType,
  RemediationItemCounts,
} from '@libs/types';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { Badge } from '@components/Badge';
import { BorderedSelect } from '@components/BorderedSelect';
import { DataTable, type DataTableColumn } from '@components/DataTable';
import { ListPagination } from '@components/ListPagination';
import { PageHeader } from '@components/PageHeader';
import { useGetAllAssessments } from '@features/assessment';
import { AssessmentStatus, LimitPerPage, Order, RemediationRunStatus } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  ArrowCounterClockwiseIcon,
  ArrowRightIcon,
  ChecksIcon,
  ClockCounterClockwiseIcon,
  FileMagnifyingGlassIcon,
  FilesIcon,
  MagnifyingGlassIcon,
  SparkleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useDebounce } from '@uidotdev/usehooks';

import { useRemediationRunStatusWatch } from '../api/remediation-api';
import {
  RemediatedAssessmentsTable,
  type RemediatedRow,
} from '../components/RemediatedAssessmentsTable';
import { RemediationHistoryDialog } from '../components/RemediationHistoryDialog';
import { RemediationListEmptyState } from '../components/RemediationListEmptyState';
import {
  ASSESSMENT_STATE_DOT,
  AssessmentRemediationBadge,
  type AssessmentRemediationState,
  ATTENTION_STATES,
} from '../components/RemediationStatusBadge';
import { RunOutcomeBreakdown, RunOutcomeLegend } from '../components/RunOutcomeBreakdown';
import { ScoreBar } from '../components/ScoreBar';
import { useRemediationLineages } from '../hooks/useRemediationLineages';

type SortKey = 'recent' | 'score';

/** The status filter's options: every remediation state a row can be in. */
type StateFilter = 'all' | AssessmentRemediationState;

const STATE_FILTERS: StateFilter[] = [
  'all',
  'none',
  'inProgress',
  'needsReview',
  'assessing',
  'reassessing',
  'partial',
  'full',
  'failed',
];

const PAGE_SIZE = LimitPerPage.TEN;

/**
 * How many assessments still being graded to pull alongside the completed ones.
 *
 * A separate query rather than a widened filter, because the endpoint takes one
 * status and this page needs two — and teaching it a list would be a backend
 * change in service of a display concern. These rows are transient and few, so
 * one small page of them is enough; they are shown only on page 1 (see
 * `rows`), which keeps the pager describing exactly the set it paginates.
 */
const IN_FLIGHT_FETCH_LIMIT = LimitPerPage.TEN;

/**
 * A run's status plus its outcome counts and what the assessment still shows, as
 * one row state.
 *
 * `full` is the claim that has to be earned, and a run alone cannot earn it. A
 * run is scoped to the documents the user ticked, so "the run completed
 * cleanly" and "this assessment has nothing left to remediate" are different
 * statements — remediating one document out of six ends in a clean COMPLETED
 * run while five gaps stand untouched. Reporting that as "Fully remediated" next
 * to a 97% score and 33 open gaps is exactly the kind of quiet overstatement a
 * compliance product cannot afford, so the assessment's own gap count has to
 * agree before the row makes that claim.
 *
 * `needsReview` and `failed` come straight off the status: they are the two
 * states the run itself declares.
 */
const stateForRow = (
  assessment: Assessment,
  run: { status: RemediationRunStatus; itemCounts: RemediationItemCounts } | undefined,
): AssessmentRemediationState => {
  // Still being graded. Remediation cannot start here whatever else is true —
  // the scope is built from a frozen `results` blob this assessment does not
  // have yet — so both of these states are read-only by construction.
  //
  // Which of the two it is, is answered by the runs we already hold: a run is
  // pinned to the lineage it came from, so a lineage with one has been
  // remediated before and this grading is the tail of that. A lineage without
  // one is simply an assessment being graded for the first time.
  if (assessment.status !== AssessmentStatus.COMPLETED) {
    return run ? 'reassessing' : 'assessing';
  }

  if (!run) return 'none';

  switch (run.status) {
    case RemediationRunStatus.PENDING:
    case RemediationRunStatus.PROCESSING:
    case RemediationRunStatus.FINALIZING:
      return 'inProgress';
    case RemediationRunStatus.READY_FOR_REVIEW:
      return 'needsReview';
    case RemediationRunStatus.FAILED:
      return 'failed';
    default:
      // COMPLETED. Partial if the run lost documents, or if the version still
      // owes anything — either way there is more the user can close, and the row
      // has to keep offering them the way to do it.
      //
      // Missing documents count as owed, alongside gaps. They are different
      // debts: a gap is a shortfall inside a document that exists, a missing
      // document was never supplied at all. Reading `gaps` alone called an
      // assessment "Fully remediated" while the framework was still expecting
      // four documents it had never seen — and `full` is the one state that
      // stops offering another pass, so the claim was also the dead end.
      return run.itemCounts.failed > 0 ||
        (assessment.gaps ?? 0) > 0 ||
        (assessment.missingDocuments ?? 0) > 0
        ? 'partial'
        : 'full';
  }
};

interface ResumeAction {
  /**
   * Already translated, not a key. "Review 3 documents" is a plural form and
   * needs its count at lookup time, so resolving in the caller would mean
   * either leaking the count out or losing the plural.
   */
  label: string;
  icon: ReactNode;
  variant: 'ai' | 'primary' | 'secondary';
  onClick: () => void;
}

/**
 * Where this row is on the journey, and the one button that continues it.
 *
 * Remediation runs scope → run → review → placeholders → summary, and a row is
 * only ever at one point on it. The action column used to stack two or three
 * buttons and let the user work out which applied; this answers instead.
 *
 * Ordered by what is owed soonest. A run waiting on a decision outranks
 * everything, because nothing else can proceed until it is made — and a
 * half-reviewed run is not an invitation to start another. Only once no run
 * wants anything does the assessment's own debt decide: gaps or missing
 * documents mean there is work to scope, and their absence means there is
 * nothing to remediate and the run's outcome is all that is left to look at.
 */
function resumeAction(input: {
  state: AssessmentRemediationState;
  assessment: Assessment;
  runId: string | undefined;
  counts: RemediationItemCounts | undefined;
  t: (key: SingularKeys, options?: Record<string, unknown>) => string;
  go: {
    scope: () => void;
    run: () => void;
    review: () => void;
    summary: () => void;
    assessment: () => void;
  };
}): ResumeAction | null {
  const { state, assessment, runId, counts, t, go } = input;

  // Being graded. The scope is built from a frozen `results` blob this
  // assessment does not have yet, so every onward step would fail.
  if (state === 'assessing' || state === 'reassessing') {
    return {
      label: t('app.remediation.list.viewAssessment'),
      icon: <FileMagnifyingGlassIcon size={16} />,
      variant: 'secondary',
      onClick: go.assessment,
    };
  }

  if (state === 'needsReview' && runId) {
    return {
      // A run reaches this state either with documents to decide or with every
      // one already decided and waiting to be applied. Same screen, different
      // sentence — a fully-decided run saying "Review 0 documents" was a button
      // whose own label said there was nothing behind it.
      label: counts?.reviewable
        ? t('app.remediation.list.reviewDocuments' as SingularKeys, {
            count: counts.reviewable,
          })
        : t('app.remediation.list.applyChanges'),
      icon: <ChecksIcon size={16} />,
      variant: 'primary',
      onClick: go.review,
    };
  }

  if (state === 'inProgress' && runId) {
    return {
      label: t('app.remediation.list.resume'),
      icon: <ArrowRightIcon size={15} />,
      variant: 'secondary',
      onClick: go.run,
    };
  }

  if (state === 'failed') {
    return {
      // Try again, not Resume: FAILED is absorbing on the backend, so there is
      // no run left to continue — this starts a new one from the picker.
      label: t('app.remediation.list.tryAgain'),
      icon: <ArrowCounterClockwiseIcon size={16} />,
      variant: 'primary',
      onClick: go.scope,
    };
  }

  const owes = (assessment.gaps ?? 0) > 0 || (assessment.missingDocuments ?? 0) > 0;
  if (owes) {
    return {
      label: t('app.remediation.list.remediate'),
      icon: <SparkleIcon size={15} weight="fill" />,
      variant: 'ai',
      onClick: go.scope,
    };
  }

  // Nothing left to remediate. If a run produced this, its summary is the last
  // step of that journey and the only thing still worth opening.
  if (runId) {
    return {
      label: t('app.remediation.list.viewSummary'),
      icon: <FilesIcon size={16} />,
      variant: 'secondary',
      onClick: go.summary,
    };
  }

  return {
    label: t('app.remediation.list.viewAssessment'),
    icon: <FileMagnifyingGlassIcon size={16} />,
    variant: 'secondary',
    onClick: go.assessment,
  };
}

/**
 * How many assessments to pull while a status filter is on.
 *
 * The remediation state is not something the assessments endpoint knows about,
 * so the filter has to run here — and filtering a server-paginated page would
 * answer the wrong question, showing "3 of 10 rows" against a pager that still
 * counts all of them. So a filtered view fetches the endpoint's maximum page in
 * one go and paginates what it holds. The unfiltered view is untouched and stays
 * server-paginated.
 *
 * The cap is the endpoint's own (`LimitPerPage.FIFTY`): a tenant with more than
 * fifty completed assessments would filter over the fifty most recent. Lifting
 * it means teaching the assessments query about remediation runs.
 */
const FILTER_FETCH_LIMIT = LimitPerPage.FIFTY;

/**
 * Sort is server-side, not client-side.
 *
 * With the table paginated, ordering the ten rows that happened to come back
 * would answer the wrong question: "lowest score first" has to mean the lowest
 * of every completed assessment, not the lowest of the most recent ten. The
 * list endpoint already sorts on both columns (`ASSESSMENTS_SORT_BY_TYPES`).
 *
 * `recent` maps onto `updatedAt` because that is what the endpoint offers; for
 * a COMPLETED assessment it tracks `completedAt` closely, and it is the same
 * ordering the assessments home uses.
 */
const SORT_QUERY: Record<SortKey, { sortBy: AssessmentsSortByType; order: Order }> = {
  recent: { sortBy: 'updatedAt', order: Order.DESC },
  score: { sortBy: 'score', order: Order.ASC },
};

/** Shows ~9 rows of the 10-row page before the body starts scrolling. */
const TABLE_HEIGHT = 560;

/**
 * Narrowest the table may get before the card scrolls sideways. Sized from the
 * two columns that cannot compress — the status pill and the action pair — so
 * shrinking the window scrolls rather than overlapping them.
 */
const TABLE_MIN_WIDTH = 1060;

/**
 * Row actions stack, so the column only has to fit one button rather than two
 * side by side — which is what let the table's minimum width come back down.
 * Both share this width so the stack reads as one block instead of a ragged
 * edge.
 */
const ACTION_BUTTON_WIDTH = 150;

/** Trimmed padding so a stacked pair does not make every row tall. */
const ACTION_BUTTON_SX = {
  width: ACTION_BUTTON_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  py: 0.625,
  fontSize: 13,
} as const;

/**
 * Screen 2 — Documents Remediation.
 *
 * The tenant's COMPLETED assessments with a Remediate action per row, matching
 * the design. Deliberately not a list of remediation *runs*: the question a
 * user arrives with is "which of my assessments still has gaps I can close",
 * and a run only exists after they have answered it.
 *
 * Per-row remediation status is derived from the tenant's runs, matched by
 * *lineage* rather than by assessment id: finalizing a run forks version N+1,
 * so the version that owns a completed run is never the latest one, and this
 * table only ever holds latest versions. Matching on id alone therefore found
 * nothing once a run finished — which is why a remediated assessment used to
 * read as "not yet remediated" the moment its run completed.
 */
export const RemediationListPage: FC = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTrans();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('recent');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 400);
  /** Lineage root whose history is open, or null. */
  /**
   * Which table is on screen.
   *
   * Two stacked tables asked the reader to work out which one their
   * assessment was in — and an assessment that is remediated but unfinished
   * is legitimately in both, which made the stack actively confusing. Tabs
   * make it a choice of question instead of a scroll: what still needs
   * doing, or what has already been produced.
   */
  const [tab, setTab] = useState<'queue' | 'remediated'>('queue');
  const [historyRoot, setHistoryRoot] = useState<{ rootId: string; title: string } | null>(null);

  const filteringByState = stateFilter !== 'all';

  // Narrowing the result set moves the row you were looking at, so page 4 of
  // the old set is meaningless against the new one. Reset rather than land the
  // user on an empty page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortBy, stateFilter]);

  const { data, isLoading, isError } = useGetAllAssessments({
    status: AssessmentStatus.COMPLETED,
    searchBy: debouncedSearch.trim() || undefined,
    // A filtered view holds the whole working set and pages it here; see
    // FILTER_FETCH_LIMIT for why the two modes differ.
    page: filteringByState ? 1 : page,
    limit: filteringByState ? FILTER_FETCH_LIMIT : PAGE_SIZE,
    ...SORT_QUERY[sortBy],
  });

  /**
   * Assessments still being graded.
   *
   * Here because of what happens at the end of a remediation: finalizing forks
   * version N+1 and submits it, so for the length of an AI run the origin is no
   * longer the latest version and the successor is not COMPLETED. The lineage
   * silently left this page, which reads as having lost the remediation.
   *
   * They also answer the opposite question — an assessment being graded for the
   * first time shows up as visibly not-yet-remediable, instead of appearing out
   * of nowhere once it completes.
   */
  const { data: inFlightData } = useGetAllAssessments({
    status: AssessmentStatus.PROCESSING,
    searchBy: debouncedSearch.trim() || undefined,
    page: 1,
    limit: IN_FLIGHT_FETCH_LIMIT,
    ...SORT_QUERY[sortBy],
  });

  const { byRoot: lineages, isLoading: runsLoading } = useRemediationLineages();

  /**
   * Keep the rows moving while a run works.
   *
   * Here rather than inside `useRemediationLineages` because watching is a
   * property of this screen, not of deriving lineages: this is the page a user
   * sits in front of waiting for a run to finish, and nothing else tells them it
   * has — there is no notification channel for remediation
   * (docs/03-development/remediation-frontend-gaps.md, G7).
   */
  useRemediationRunStatusWatch();

  /**
   * Both queries, because a row is only half a row without the runs.
   *
   * Until they land every row derives `state: 'none'` — so the table read "Not
   * remediated" for everything, offered an AI Remediate button on assessments
   * that were already remediated, and, with a status filter on from the start,
   * showed the wrong set entirely before correcting itself a moment later.
   */
  const loading = isLoading || runsLoading;

  // Already ordered by the endpoint — see SORT_QUERY. Re-sorting here would
  // only reshuffle the current page against a global ordering.
  const completedRows = useMemo(() => data?.rows ?? [], [data]);
  const meta = data?.meta;

  /**
   * Grading assessments, first and only on page 1.
   *
   * They lead because they are the volatile rows — the ones whose state is
   * changing while the user watches — and because on a later page they would be
   * a repeat of what page 1 already showed. Confining them to page 1 also keeps
   * the pager honest: it describes the completed set it actually paginates, and
   * these are not part of it.
   */
  const inFlightRows = useMemo(
    () => (page === 1 ? (inFlightData?.rows ?? []) : []),
    [inFlightData, page],
  );

  const remediatedRows = useMemo(
    () =>
      [...inFlightRows, ...completedRows]
        .map((assessment) => ({
          assessment,
          run: lineages.get(assessment.parentAssessmentId ?? assessment.id)?.produced,
        }))
        .filter((row): row is RemediatedRow => Boolean(row.run)),
    [inFlightRows, completedRows, lineages],
  );

  const rowsWithState = useMemo(
    () =>
      [...inFlightRows, ...completedRows]
        .map((assessment) => {
          const run = lineages.get(assessment.parentAssessmentId ?? assessment.id)?.current;
          return {
            assessment,
            // Derived where the run and its assessment are both in hand: whether
            // a finished run left the assessment fully remediated, and whether a
            // grading assessment is a re-grade or a first pass, are both
            // questions about the pair — see `stateForRow`.
            state: stateForRow(assessment, run),
            runId: run?.runId,
            counts: run?.itemCounts,
          };
        })
        // Only a version that owes nothing leaves the queue — no gaps and no
        // missing documents. Having been remediated is not the same as being
        // finished: a run closes having written three documents while five gaps
        // stand untouched, and dropping that row was what stopped the user
        // continuing. It stays here, and appears under Remediated as well.
        .filter((row) => row.state !== 'full'),
    [inFlightRows, completedRows, lineages],
  );

  const matchingRows = useMemo(
    () => (filteringByState ? rowsWithState.filter((r) => r.state === stateFilter) : rowsWithState),
    [rowsWithState, filteringByState, stateFilter],
  );

  /**
   * Rows waiting on a person, counted across everything currently loaded.
   *
   * Only meaningful on the page in hand — the unfiltered table is
   * server-paginated, so this counts the rows fetched rather than the tenant's
   * whole history. It is a shortcut to the filter, not a KPI, so undercounting a
   * later page is preferable to a second request purely to make a number bigger.
   */
  const attentionCount = useMemo(
    () => rowsWithState.filter((r) => ATTENTION_STATES.includes(r.state)).length,
    [rowsWithState],
  );

  // Client-side paging, and only in the filtered mode — the unfiltered table
  // pages on the server and `meta` already describes it.
  const clientTotalPages = Math.max(1, Math.ceil(matchingRows.length / PAGE_SIZE));
  const rows = filteringByState
    ? matchingRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : matchingRows;

  const hasFilters = Boolean(debouncedSearch.trim()) || filteringByState;

  // Column budget. `Badge` never shrinks or ellipsizes its label by design
  // (see Badge.tsx), so the status column has to be wide enough for the longest
  // pill — "Remediation in progress" — or it spills over the action column
  // instead of clipping. The action column only has to fit one button now that
  // the pair stacks. Below TABLE_MIN_WIDTH the card scrolls horizontally rather
  // than squeezing either of them.
  const columns: DataTableColumn[] = [
    {
      key: 'assessment',
      label: t('app.remediation.list.col.assessment'),
      width: '26%',
      skeleton: 'title',
    },
    { key: 'completed', label: t('app.remediation.list.col.completed'), width: '11%' },
    {
      key: 'version',
      label: t('app.remediation.list.col.version'),
      width: '6%',
      skeleton: 'number',
    },
    { key: 'score', label: t('app.remediation.list.col.score'), width: '13%', skeleton: 'bar' },
    { key: 'gaps', label: t('app.remediation.list.col.gaps'), width: '6%', skeleton: 'number' },
    {
      key: 'status',
      label: t('app.remediation.list.col.status'),
      width: '19%',
      skeleton: 'badge',
    },
    {
      key: 'action',
      label: '',
      width: '19%',
      align: 'right',
      skeleton: 'actionStack',
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: 'background.paper',
        borderRadius: 4,
      }}
    >
      {/* Same frame as the assessments home: a padded card the table fills,
          rather than a centred column — capping it crowds the score bars and
          status pills while the window sits half empty. */}
      <Box
        sx={{
          width: '100%',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <PageHeader
          title={t('app.remediation.list.title')}
          titleAdornment={
            <Badge
              label={t('app.remediation.list.aiBadge')}
              icon={<SparkleIcon size={14} weight="fill" />}
              sx={{
                backgroundColor: 'ocean.100',
                color: 'ocean.700',
                border: (theme) => `1px solid ${theme.palette.ocean[300]}`,
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'ocean.700' },
              }}
            />
          }
          description={t('app.remediation.list.subtitle')}
        />

        {/* Search + sort, styled as the assessments filter bar. The design also
            has a framework filter; the tenant has a single framework today, so
            it would be a control that does nothing.

            Two groups rather than four peers, as on the document hub's filter
            bar: the controls share a row that flexes, and the call to action is
            its sibling. Percentage widths were what broke this — 40/25/25 plus
            gaps leaves under a tenth of the row for a button that needs about
            200px, so it wrapped alone onto a second line at every width short
            of an ultrawide monitor. Sizing the controls by flex instead lets
            them give the button its space on the same row.

            The group's basis is the three controls at their minimums plus the
            gaps between them (220 + 140 + 170 + 2×16). That is what orders the
            two wraps: the button drops to its own line while the controls still
            fit as a row, rather than the controls breaking up around a button
            left stranded mid-bar. */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              flex: '1 1 562px',
              minWidth: 0,
            }}
          >
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('app.remediation.list.searchPlaceholder')}
              variant="outlined"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlassIcon size={20} color={theme.palette.text.primary} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearch('')} size="small">
                        <XIcon size={20} color={theme.palette.text.primary} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                },
              }}
              // Twice the growth of either select, so a roomy row still reads as
              // the 40/25/25 the design asks for while a cramped one can hand
              // the space back.
              sx={{
                flex: '2 1 220px',
                minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.grey[200],
                },
              }}
            />
            <BorderedSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortKey)}
              minWidth={140}
              sx={{ flex: '1 1 140px', minWidth: '140px' }}
            >
              <MenuItem value="recent">{t('app.remediation.list.sortRecent')}</MenuItem>
              <MenuItem value="score">{t('app.remediation.list.sortScore')}</MenuItem>
            </BorderedSelect>
            {/* Filters the same column the table shows, using the vocabulary the
                badge already uses — so what a user filters by reads exactly like
                what they were looking at when they reached for it. */}
            <BorderedSelect
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as StateFilter)}
              minWidth={170}
              sx={{ flex: '1 1 170px', minWidth: '170px' }}
            >
              {STATE_FILTERS.map((f) => (
                <MenuItem key={f} value={f}>
                  {t(
                    f === 'all'
                      ? 'app.remediation.list.stateFilterAll'
                      : (`app.remediation.list.state.${f}` as SingularKeys),
                  )}
                </MenuItem>
              ))}
            </BorderedSelect>
          </Box>

          {/* A shortcut to the two states that are waiting on a person, since
              "is anything blocked on me" is the question this page is most often
              opened to answer — and answering it otherwise means reading every
              row's status. Hidden at zero rather than shown as "(0)": an empty
              call to action is noise.

              No `marginInlineStart: 'auto'`: the controls group already grows
              into the free space, which puts this at the end of the row without
              also pinning it to the far edge on the narrow screens where it
              wraps. There it starts the second row under the search field
              instead, which is what an orphaned right-aligned button was
              getting wrong. */}
          {attentionCount > 0 && (
            <Button
              variant={stateFilter === 'needsReview' ? 'primary' : 'secondary'}
              startIcon={<WarningIcon size={16} />}
              onClick={() => setStateFilter(stateFilter === 'needsReview' ? 'all' : 'needsReview')}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {t('app.remediation.list.needsAttention', { count: attentionCount })}
            </Button>
          )}
        </Box>

        {/* Only once there is something behind the second tab. A tab bar with
            one usable tab is furniture, not navigation. */}
        {remediatedRows.length > 0 && (
          <Tabs
            value={tab}
            onChange={(_e, next) => setTab(next as 'queue' | 'remediated')}
            sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
          >
            <Tab value="queue" label={t('app.remediation.list.tab.queue')} />
            <Tab
              value="remediated"
              label={`${t('app.remediation.remediated.title')} (${remediatedRows.length})`}
            />
          </Tabs>
        )}

        {tab === 'queue' && (
        <DataTable
          columns={columns}
          minWidth={TABLE_MIN_WIDTH}
          // Fixed height: the body scrolls inside the card against a sticky
          // header, so the page itself does not grow with the row count and the
          // filters stay put while paging through assessments.
          // Released when there is nothing to scroll: 560px of empty card under a
          // three-line placeholder reads as a table that failed to load.
          height={rows.length === 0 && !loading && !isError ? undefined : TABLE_HEIGHT}
          isLoading={loading}
          skeletonRows={PAGE_SIZE}
          isError={isError}
          errorLabel={t('app.remediation.list.loadError')}
          isEmpty={rows.length === 0}
          emptyContent={
            <RemediationListEmptyState
              filtered={hasFilters}
              onAction={() => {
                if (!hasFilters) {
                  navigate(routes.assessmentList());
                  return;
                }
                // Both of them: "Clear filters" that left the status filter on
                // would empty the table again and look broken.
                setSearch('');
                setStateFilter('all');
              }}
            />
          }
          // Anchored inside the card by DataTable's footer slot, so it stays put
          // while the body scrolls. Hidden on a single page — a pager that can
          // only ever point at itself is noise. A filtered view counts what it
          // is actually showing, so the pager and the rows never disagree.
          // The legend rides in the footer slot so it stays anchored to the card
          // while the body scrolls — the counts it explains repeat on every row,
          // so a key that scrolled away with them would be useless exactly when
          // it was needed. Suppressed on an empty or broken table, where there
          // are no counts to explain.
          footerLead={rows.length > 0 && !loading && !isError ? <RunOutcomeLegend /> : undefined}
          footer={
            filteringByState ? (
              clientTotalPages > 1 ? (
                <ListPagination
                  page={page}
                  totalPages={clientTotalPages}
                  isLoading={loading}
                  onPageChange={setPage}
                  summary={t('app.remediation.list.pagination.summary', {
                    page,
                    totalPages: clientTotalPages,
                    totalCount: matchingRows.length,
                  })}
                  sx={{ mt: 0 }}
                />
              ) : undefined
            ) : meta && meta.totalPages > 1 ? (
              <ListPagination
                page={meta.currentPage}
                totalPages={meta.totalPages}
                hasPrevPage={meta.hasPrevPage}
                hasNextPage={meta.hasNextPage}
                isLoading={loading}
                onPageChange={setPage}
                summary={t('app.remediation.list.pagination.summary', {
                  page: meta.currentPage,
                  totalPages: meta.totalPages,
                  totalCount: meta.totalCount,
                })}
                sx={{ mt: 0 }}
              />
            ) : undefined
          }
        >
          {rows.map(({ assessment, state, runId, counts }) => (
            <AssessmentRow
              key={assessment.id}
              assessment={assessment}
              language={i18n.language}
              state={state}
              counts={counts}
              runId={runId}
              // Decided here, where the router is. Remediate always opens the
              // picker — never the run — because that is what the word means;
              // the picker carries its own way into a run already in flight.
              action={resumeAction({
                state,
                assessment,
                runId,
                counts,
                t,
                go: {
                  scope: () => navigate(routes.remediationScope(assessment.id)),
                  run: () => runId && navigate(routes.remediationRun(runId)),
                  review: () => runId && navigate(routes.remediationReview(runId)),
                  summary: () => runId && navigate(routes.remediationSummary(runId)),
                  assessment: () => navigate(routes.assessmentOverview(assessment.id)),
                },
              })}
              // Offered only where there is a history to show. The row reports
              // the newest run; this is every pass the assessment has been
              // through and what each one did.
              onViewHistory={
                (lineages.get(assessment.parentAssessmentId ?? assessment.id)?.all.length ?? 0) > 0
                  ? () =>
                      setHistoryRoot({
                        rootId: assessment.parentAssessmentId ?? assessment.id,
                        title: assessment.title,
                      })
                  : undefined
              }
            />
          ))}
        </DataTable>
        )}

        {/* Its own tab rather than stacked under the queue. An assessment that
            has been remediated but is not finished belongs in both, so stacking
            them made the reader work out which table they were looking at. */}
        {tab === 'remediated' && remediatedRows.length > 0 && (
          <Box>
            <Typography
              variant="textSM_regular"
              sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
            >
              {t('app.remediation.remediated.subtitle')}
            </Typography>
            <RemediatedAssessmentsTable
              rows={remediatedRows}
              language={i18n.language}
              isLoading={loading}
              onOpenAssessment={(id) => navigate(routes.assessmentOverview(id))}
            />
          </Box>
        )}

        {historyRoot && (
          <RemediationHistoryDialog
            open
            onClose={() => setHistoryRoot(null)}
            assessmentTitle={historyRoot.title}
            runs={lineages.get(historyRoot.rootId)?.all ?? []}
            language={i18n.language}
            onOpenRun={(runId) => {
              setHistoryRoot(null);
              navigate(routes.remediationRun(runId));
            }}
          />
        )}

      </Box>
    </Box>
  );
});

const AssessmentRow: FC<{
  assessment: Assessment;
  language: string;
  /** Derived by the caller, which holds the tenant's runs — see `useRemediationLineages`. */
  state: AssessmentRemediationState;
  /** The run's outcome tallies. Absent only when there is no run at all. */
  counts?: RemediationItemCounts;
  /** The run this row is about, when it has one — the journey needs its id. */
  runId?: string;
  /**
   * The one action that continues this row's journey, already decided.
   *
   * Passed in rather than derived here because only the caller holds the router
   * — and because `resumeAction` is where the ordering rule lives, which is the
   * part worth reading in one place.
   */
  action: ResumeAction | null;
  /** Absent when this lineage has no runs to show a history of. */
  onViewHistory?: () => void;
}> = observer(
  ({
    assessment,
    language,
    state,
    counts,
    action,
    onViewHistory,
  }) => {
    const theme = useTheme();
    const { t } = useTrans();

    const needsAttention = ATTENTION_STATES.includes(state);
    // The assessment itself is with the AI. Nothing on this row can start a
    // remediation until it comes back, whichever of the two reasons it is.
    const isGrading = state === 'assessing' || state === 'reassessing';

    return (
      <TableRow
        hover
        sx={
          needsAttention
            ? {
                // A marker on the leading edge, so a row that wants something
                // from the user is findable while scanning a page of otherwise
                // identical rows. Colour-coded to match the badge, but the badge
                // still carries the words — this is a locator, not the message.
                '& > td:first-of-type': {
                  boxShadow: `inset 3px 0 0 ${
                    state === 'failed' ? theme.palette.error.main : theme.palette.warning.main
                  }`,
                },
              }
            : undefined
        }
      >
        <TableCell sx={{ pl: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                flexShrink: 0,
                // Aligns with the first line of a title that now wraps.
                mt: 0.75,
                backgroundColor: ASSESSMENT_STATE_DOT[state](theme),
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
                minWidth: 0,
              }}
            >
              {/* Wraps rather than truncating: the title is what identifies the
                  row, and a clipped one made two similarly-named assessments
                  indistinguishable. The table's fixed height already scrolls, so
                  a taller row costs nothing. */}
              <Typography
                variant="textSM_semiBold"
                sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
              >
                {assessment.title}
              </Typography>
              {assessment.language?.label && (
                <Badge
                  variant="Language"
                  label={assessment.language.label}
                  sx={{
                    height: '22px',
                    flexShrink: 0,
                    textTransform: 'none',
                    backgroundColor: '#F2F2F2',
                    color: 'grey.700',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    px: 1,
                  }}
                />
              )}
            </Box>
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }} noWrap>
            {assessment.completedAt
              ? new Date(assessment.completedAt).toLocaleDateString(language)
              : '—'}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
            v{assessment.version}
          </Typography>
        </TableCell>

        <TableCell>
          <ScoreBar score={assessment.score} />
        </TableCell>

        {/* Total identified gaps, carried on the list row itself — the results
            blob it comes from was already being selected, so no per-row fetch.
            Null means the assessment was never graded, which is not the same as
            zero and must not render as it. */}
        <TableCell>
          <Typography
            variant="textSM_regular"
            sx={{
              color: assessment.gaps == null ? 'text.tertiary' : 'text.primary',
              fontWeight: assessment.gaps ? 600 : 400,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {assessment.gaps ?? '—'}
          </Typography>
        </TableCell>

        {/* The pill sets the cell's floor rather than being clipped by it — see
            the column budget above. Under it, what the badge cannot say: how the
            run's documents actually came out. A failed document inside an
            otherwise fine run was invisible from this table until this existed. */}
        <TableCell>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <AssessmentRemediationBadge state={state} reviewCount={counts?.reviewable} />
            </Box>
            {/* A grading assessment's counts belong to the run that produced it,
                not to what is happening now, so they would read as live progress
                for work that has already finished. */}
            {counts && !isGrading && <RunOutcomeBreakdown counts={counts} />}
            {(state === 'failed' || isGrading) && (
              <Typography
                variant="textXS_regular"
                sx={{ display: 'block', mt: 0.375, color: 'text.tertiary' }}
              >
                {t(
                  state === 'failed'
                    ? 'app.remediation.list.failedHint'
                    : state === 'reassessing'
                      ? // Says where the work went, because the alternative —
                        // this lineage silently leaving the page for the length
                        // of an AI run — is what made users think they had lost
                        // their remediation.
                        'app.remediation.list.reassessingHint'
                      : 'app.remediation.list.assessingHint',
                )}
              </Typography>
            )}
            {/* A link rather than a third stacked button: history is context for
                the status it sits under, not a third thing the row is asking the
                user to do, and a third button would make every row taller for
                something most of them never open. */}
            {onViewHistory && (
              <Button
                variant="text"
                onClick={onViewHistory}
                startIcon={<ClockCounterClockwiseIcon size={13} />}
                sx={{
                  // `text` is not one of the theme's Button variants, so this
                  // inherits the filled root style — a dark box painted at the
                  // label's size, with the start icon's negative inline-start
                  // margin hanging outside it. Undone here, along with the
                  // zero padding that left nothing between text and edge.
                  backgroundColor: 'transparent',
                  mt: 0.375,
                  marginInlineStart: '-6px',
                  px: 0.75,
                  py: 0.25,
                  minWidth: 0,
                  fontSize: 11.5,
                  fontWeight: 600,
                  lineHeight: 1.5,
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                  // Logical, so the gap sits between icon and label in Arabic
                  // too rather than doubling up on the wrong side.
                  '& .MuiButton-startIcon': {
                    marginInlineStart: 0,
                    marginInlineEnd: '4px',
                    marginRight: 0,
                    marginLeft: 0,
                  },
                  '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
                }}
              >
                {t('app.remediation.list.viewHistory')}
              </Button>
            )}
          </Box>
        </TableCell>

        <TableCell align="right" sx={{ pr: 2 }}>
          {/* One button, because remediation is a journey and a row is only
              ever at one point on it.

              This column used to stack two or three: a primary, a "view
              documents", a "view assessment", sometimes an "add documents".
              Together they turned a single question — what do I do with this
              assessment now — into a menu the user had to read every row of.
              The row already knows where it is, so it can just say what comes
              next and take them there.

              `resumeAction` is that decision, made once from the run and the
              assessment together. */}
          {action && (
            <Button
              variant={action.variant}
              startIcon={action.icon}
              onClick={action.onClick}
              sx={ACTION_BUTTON_SX}
            >
              {action.label}
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  },
);
